import { Alert, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { ImportStateValidation, withDialogSuspenseErrorBoundary } from '@retail/components';
import { useCallback, useEffect, useMemo } from 'react';
import { getImportedFreightConditions } from '@retail/freight/utils';
import { SpreadSheetRow } from '@retail/products/types';
import { UploadContainer, useImportItemsStore } from '@retail/products/components';
import { UploadedFreight } from '@retail/freight/types';
import { FreightConditionsImportConfig } from './FreightConditionsImportConfig';
import { useConditionActions } from '@retail/calculus/context';

interface Props {
  open: boolean;
  onClose: () => void;
  supplierNumber: number;
}

const importConfig = FreightConditionsImportConfig.instance;

function UploadFreightConditionsDialog({ supplierNumber, open, onClose }: Props) {
  const t = useAppTFunction();

  const { createMultipleConditions } = useConditionActions();

  const {
    importedFile,
    importSpreadsheet,
    clearState: resetImportConditions,
    run,
  } = useImportItemsStore<UploadedFreight>();

  const validDataRows: UploadedFreight[] | undefined = useMemo(
    () => run?.rows.filter(({ status }) => status === 'ok').map(({ values }) => values),
    [run?.rows]
  );

  const handleImportFile = (inputRows: SpreadSheetRow[], file: File) => {
    importSpreadsheet(inputRows, file, importConfig);
  };

  const importedFreightConditions = useMemo(
    () => getImportedFreightConditions({ validFreights: validDataRows ?? [], supplierNumber }),
    [supplierNumber, validDataRows]
  );

  const handleUploadFreightConditions = useCallback(async () => {
    createMultipleConditions(importedFreightConditions).then(onClose);
  }, [createMultipleConditions, importedFreightConditions, onClose]);

  useEffect(() => {
    if (!open) resetImportConditions();
  }, [open, resetImportConditions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('calculus.actions.uploadFreight')}</DialogTitle>

      <DialogContent>
        <UploadContainer
          importedFile={importedFile}
          onDownloadTemplate={() =>
            importConfig.downloadTemplateAsExcel('freightConditionsTemplate')
          }
          importSpreadsheet={handleImportFile}
          resetImportedStates={resetImportConditions}
          isLarge
          renderValidation={
            run &&
            (run?.summary.status === 'success' ? (
              <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
                {t('products.uploadItems.nrItemsRead', {
                  count: run.summary.okCount,
                  totalCount: run.summary.totalCount,
                })}
              </Alert>
            ) : (
              <ImportStateValidation
                missingRequiredColumns={run.missingRequiredColumns}
                invalidDataRows={run.rows
                  .filter((row) => row.status === 'error')
                  .map((row) => ({
                    index: row.rowIndex,
                    errors: row.errors
                      .map((error) => ({
                        column: error.column,
                        reason: error.reason,
                        value: error.raw,
                      }))
                      .concat(
                        // @ts-ignore
                        (row.rowErrors ?? []).map(({ message }) => ({
                          reason: message,
                        }))
                      ),
                  }))}
              />
            ))
          }
        />
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose} size="small">
          {t('calculus.cancel')}
        </TextButton>
        <ContainedButton
          disabled={!importedFile}
          onClick={handleUploadFreightConditions}
          size="small"
          color="secondary"
        >
          {t('calculus.toApproval')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(UploadFreightConditionsDialog);
